import Phaser from "phaser";
import { pixelType, type PixelTypeRole } from "odyssey-shared";
import bitmapFontXml from "../../ui/generated/fonts/pixel.fnt" with { type: "text" };
import plainFontXml from "../../ui/generated/fonts/pixel-plain.fnt" with { type: "text" };
import { UI_ATLAS_KEY } from "./assets.ts";
import type { WorldUiModule } from "./hook.ts";

/**
 * The pixel font on the canvas (ADR-061 decisions 2 and 7 as amended): the bitmap font `bun run ui:build`
 * builds from Pixelify Sans's measured glyph grid, its sheet packed into the UI atlas as `ui/font/pixel` and
 * its BMFont XML bundled with the client, parsed with Phaser's `ParseFromAtlas` into the bitmap font cache
 * under `PIXEL_FONT_KEY`. Every glyph carries a 1 px `#000000` outline around white ink, so a tint colours
 * the ink and leaves the outline black, and the advances are the grid's, so a `BitmapText` sets a word on the
 * same pixels and baseline as the DOM's web font rebuilt from the same grid. A second font, `PLAIN_FONT_KEY`, holds
 * the same glyphs and advances with no outline, its sheet `ui/font/pixel-plain`, for dark text on a light fill.
 *
 * @example
 * ```ts
 * const name = new Phaser.GameObjects.BitmapText(scene, x, y, PIXEL_FONT_KEY, "Ada", pixelFontSize("nameplate")).setOrigin(0).setTint(0xf4ecd6);
 * context.add(name);
 * ```
 */

/**
 * The key the pixel font is cached under.
 */
export const PIXEL_FONT_KEY = "pixel";

/**
 * The atlas frame holding the font's sheet.
 */
export const PIXEL_FONT_FRAME = "ui/font/pixel";

/**
 * The key the plain font is cached under: the same glyphs and advances with no outline, for dark text on a light
 * fill (the chat bubble's; ADR-068's amendment of 2026-09-27), where the outline would merge with the ink.
 */
export const PLAIN_FONT_KEY = "pixel-plain";

/**
 * The atlas frame holding the plain font's sheet.
 */
export const PLAIN_FONT_FRAME = "ui/font/pixel-plain";

/**
 * Each bitmap font the module registers: its cache key, its atlas frame, its XML and the key its XML is cached
 * under.
 */
const FONTS = [
  { key: PIXEL_FONT_KEY, frame: PIXEL_FONT_FRAME, xml: bitmapFontXml, xmlKey: "pixel-font-xml" },
  { key: PLAIN_FONT_KEY, frame: PLAIN_FONT_FRAME, xml: plainFontXml, xmlKey: "pixel-plain-font-xml" },
] as const;

/**
 * The `BitmapText` size of a pixel type role: the native size times the role's multiple, so the glyphs are
 * drawn at a whole multiple of their grid.
 *
 * @param role The role.
 * @returns The size to pass to `BitmapText`.
 */
export function pixelFontSize(role: PixelTypeRole): number {
  return pixelType(role).size;
}

/**
 * Parses the bundled XML and the atlas frame of each bitmap font, the outlined and the plain, into the bitmap font
 * cache, once per game.
 *
 * @param scene A scene whose `ui` texture has loaded.
 * @throws Error when the atlas lacks a font's frame or its XML does not parse.
 */
export function registerPixelFont(scene: Phaser.Scene): void {
  for (const font of FONTS) {
    if (scene.cache.bitmapFont.exists(font.key)) {
      continue;
    }
    if (!scene.cache.xml.exists(font.xmlKey)) {
      const xml = new DOMParser().parseFromString(font.xml, "text/xml");
      if (xml.getElementsByTagName("parsererror").length > 0) {
        throw new Error(`the ${font.key} font's XML does not parse`);
      }
      scene.cache.xml.add(font.xmlKey, xml);
    }
    if (!Phaser.GameObjects.BitmapText.ParseFromAtlas(scene, font.key, UI_ATLAS_KEY, font.frame, font.xmlKey)) {
      throw new Error(`the UI atlas has no ${font.frame} frame for the ${font.key} font`);
    }
  }
}

/**
 * Registers the pixel font once the UI atlas has loaded. It is listed after the atlas in `modules.ts`, and
 * the gallery's pages list it after the atlas, so every surface after it can set `BitmapText` in
 * `PIXEL_FONT_KEY`.
 */
export const pixelFont: WorldUiModule = {
  id: "pixel-font",
  /**
   * Parses the font from the loaded atlas.
   *
   * @param context The scene.
   * @returns An empty surface.
   */
  create(context) {
    registerPixelFont(context.scene);
    return {};
  },
};
